import {
  DotsThree,
  Exam,
  Eyes,
  MagnifyingGlass,
  Printer,
} from "@phosphor-icons/react";
import Link from "astro-typesafe-routes/link/react";
import clsx from "clsx";
import { addDays } from "date-fns";
import { noop, times } from "lodash-es";
import Rand from "rand-seed";
import { memo } from "react";

import { DialogHeader } from "~/components/DialogHeader";
import { formatDate } from "~/utils/date";

import { AssignmentStep } from "./AssignmentStep";

const MOCK_SUBMISSIONS_START_DATE = new Date(2024, 8, 20);
const MOCK_SUBMISSIONS: {
  name: string;
  result: number;
  days: number;
}[] = [
  {
    name: "Sidney",
    result: 87,
    days: 0,
  },
  {
    name: "Hayley",
    result: 22,
    days: 0,
  },
  {
    name: "Connor",
    result: 97,
    days: 1,
  },
  {
    name: "Julia",
    result: 88,
    days: 1,
  },
  {
    name: "Taylor",
    result: 71,
    days: 2,
  },
  {
    name: "Auston",
    result: 34,
    days: 2,
  },
  {
    name: "Andrei",
    result: 37,
    days: 3,
  },
];

const CreateAssignment: React.FC = () => {
  const rng = new Rand("wcARf9sUDH");
  return (
    <>
      <DialogHeader>How to create an assignment</DialogHeader>
      <div className="grid grow max-md:divide-y md:grid-cols-2 lg:grid-cols-3 lg:divide-x">
        <AssignmentStep
          number={1}
          title="Find the right pack"
          description={
            <p>
              Start with{" "}
              <Link to="/account/packs" className="link">
                a pack you&apos;ve created
              </Link>
              , or discover a new one by browsing or searching{" "}
              <Link to="/packs" className="link">
                our library
              </Link>
              .
            </p>
          }
        >
          {times(12, (index) => (
            <div
              key={index}
              className={clsx(
                "absolute aspect-tv w-48 rounded-box bg-gradient-to-br from-purple-heart-100/50",
                index % 2 && "max-lg:hidden",
              )}
              style={{
                top: `${rng.next() * 100}%`,
                left: `${rng.next() * 100}%`,
                transform: `scale(${rng.next() * 0.5 + 0.5})`, // 50% to 100%
              }}
            />
          ))}
          <div className="input input-bordered absolute left-1/3 right-0 top-1/2 flex items-center gap-2 rounded-l-full max-lg:hidden">
            <MagnifyingGlass className="-ml-1 size-5 shrink-0" weight="bold" />
            solar system
          </div>
        </AssignmentStep>
        <AssignmentStep
          number={2}
          title={
            <>
              Click the <em>Assign</em> button
            </>
          }
          description={
            <p>
              Open the pack&apos;s action menu and select{" "}
              <strong>&ldquo;Assign.&rdquo;</strong> Configure settings like due
              date and time limit.
            </p>
          }
          className="max-lg:md:border-l"
        >
          <div className="absolute inset-0 z-0 grid grid-cols-3 grid-rows-3 gap-2 bg-gradient-to-t from-base-200 p-2 lg:pt-16">
            {times(4, (index) => (
              <div key={index} className="rounded-box bg-base-100" />
            ))}
            <div className="flex items-start justify-end rounded-box bg-base-100 p-2 lg:border">
              <div className="relative max-lg:hidden">
                <div className="btn btn-circle btn-sm">
                  <DotsThree weight="bold" className="size-5" />
                </div>
                <div className="absolute right-0 top-full z-10 -mx-1 my-1 min-w-48 rounded-box border bg-base-100 shadow">
                  <ul className="menu">
                    <li>
                      <div>
                        <Eyes className="size-4" weight="bold" />
                        Preview
                      </div>
                    </li>
                    <li>
                      <div className="active">
                        <Exam className="size-4" weight="bold" />
                        Assign
                      </div>
                    </li>
                    <li>
                      <div>
                        <Printer className="size-4" weight="bold" />
                        Print
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            {times(4, (index) => (
              <div key={index} className="rounded-box bg-base-100" />
            ))}
          </div>
        </AssignmentStep>
        <AssignmentStep
          number={3}
          title="Track progress"
          description={
            <>
              <p>
                Share the assignment link with students and monitor their
                results in real time.
              </p>
              <p>
                <strong>New to assignments?</strong>{" "}
                <Link
                  to="/support/guides/[...slug]"
                  className="link"
                  params={{
                    "...slug": "introducing-assignments",
                  }}
                >
                  Our guide
                </Link>{" "}
                breaks down everything from setup to sharing.
              </p>
            </>
          }
          className="max-lg:md:col-span-2 max-lg:md:border-t"
        >
          <div className="absolute inset-y-0 left-1/4 right-0">
            <div className="grid h-1/2 grid-cols-3 gap-2 pb-4">
              {times(9, noop).map((_, index, array) => {
                return (
                  <div
                    key={index}
                    className="rounded bg-base-200"
                    style={{ opacity: 1 - index / array.length }}
                  />
                );
              })}
            </div>
            <table className="table max-lg:text-transparent">
              <thead>
                <tr className="max-lg:invisible">
                  <th>Name</th>
                  <th>Result</th>
                  <th>Date completed</th>
                </tr>
              </thead>
              <tbody>
                {MOCK_SUBMISSIONS.map((submission, index) => (
                  <tr key={index} style={{ opacity: 1 - index / 7 }}>
                    <td>{submission.name}</td>
                    <td>{submission.result}%</td>
                    <td>
                      {formatDate(
                        addDays(MOCK_SUBMISSIONS_START_DATE, submission.days),
                        "PP",
                        "en-US",
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AssignmentStep>
      </div>
    </>
  );
};

export default memo(CreateAssignment);
